import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, KeyRound, ChevronLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";

export default function ForgotPassword() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("request"); // request | reset | done
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mockedToken, setMockedToken] = useState(null);

  const request = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      if (data?.mocked_token) {
        setMockedToken(data.mocked_token);
        setToken(data.mocked_token);
      }
      setPhase("reset");
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: newPassword });
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-6" data-testid="forgot-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-8">
        <div className="mb-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366] mb-4" data-testid="back-to-login">
            <ChevronLeft size={13} /> {es ? "Iniciar sesión" : "Sign in"}
          </Link>
          <Logo />
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">
            {phase === "done" ? (es ? "Listo" : "Done") : es ? "Recuperar contraseña" : "Reset password"}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {phase === "request" && (es ? "Te enviaremos un token de recuperación." : "We will send you a recovery token.")}
            {phase === "reset" && (es ? "Introduce el token y tu nueva contraseña." : "Enter the token and your new password.")}
            {phase === "done" && (es ? "Contraseña actualizada. Ya puedes iniciar sesión." : "Password updated. You can now sign in.")}
          </p>
        </div>

        {phase === "request" && (
          <form onSubmit={request} className="space-y-4" data-testid="forgot-request-form">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">{es ? "Correo electrónico" : "Email"}</label>
              <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono" />
            </div>
            {error && <div data-testid="forgot-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-mono">{error}</div>}
            <button type="submit" disabled={loading} data-testid="forgot-request-btn"
              className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2.5 font-medium text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {es ? "Enviar token" : "Send token"}
            </button>
          </form>
        )}

        {phase === "reset" && (
          <form onSubmit={confirm} className="space-y-4" data-testid="forgot-reset-form">
            {mockedToken && (
              <div className="text-[11px] font-mono bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900">
                <b>DEMO MODE:</b> {es ? "token pre-llenado para pruebas. En producción llegaría por email." : "token pre-filled for demo. In production it arrives by email."}
                <br /><code className="break-all text-[10px]">{mockedToken}</code>
              </div>
            )}
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">Token</label>
              <input data-testid="forgot-token" required value={token} onChange={(e) => setToken(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">{es ? "Nueva contraseña" : "New password"}</label>
              <input data-testid="forgot-new-password" type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono" />
            </div>
            {error && <div data-testid="forgot-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-mono">{error}</div>}
            <button type="submit" disabled={loading} data-testid="forgot-confirm-btn"
              className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2.5 font-medium text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              {es ? "Actualizar contraseña" : "Update password"}
            </button>
          </form>
        )}

        {phase === "done" && (
          <div className="text-center py-6" data-testid="forgot-done">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4">
              <KeyRound size={22} />
            </div>
            <Link to="/login" className="inline-block bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-5 py-2 text-sm font-medium">
              {es ? "Iniciar sesión" : "Sign in"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
